/**
 * PointEntity - 점 도형 Entity
 */
import { SHAPES } from '../../../const'
import type { ICanvasEngineEntityController } from '../../../types/interface'
import type { Vector2d } from '../../../types'
import type { IEllipse, IRenderer } from '../../../backend/IRenderer'

/**
 * Point 생성 설정
 */
export interface PointCreateConfig {
  color: string
  radius?: number
}

/**
 * Point 초기화 설정
 */
export interface PointInitConfig {
  id?: string
  createPoint: Vector2d
  balloonPoint?: Vector2d
  config: PointCreateConfig
  layerId?: string
  pointShapeScale?: number
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  areaBorderThicknessScale?: number
  interactionMode?: boolean
  dotType?: 'dot' | 'arrow'
}

/**
 * Point 도형 생성 함수
 * @param canvasEngine - CanvasEngine 컨트롤러
 * @param initConfig - 초기화 설정
 * @returns Point 관련 노드 핸들
 */
export function createPointShape(
  renderer: IRenderer,
  canvasEngine: ICanvasEngineEntityController,
  initConfig: PointInitConfig,
): {
  shape: IEllipse
  color: string
} {
  const globalPointSize = initConfig.pointShapeScale ?? canvasEngine.pointShapeScale
  const color = initConfig.config.color

  // Point Ellipse 생성
  const point = renderer.createEllipse({
    x: 0,
    y: 0,
    radiusX: globalPointSize,
    radiusY: globalPointSize,
    fill: color,
    hitArea: { kind: 'circle', radius: globalPointSize * 8 }, // 핸들 영역 대폭 확대
  })

  return {
    shape: point,
    color,
  }
}

/**
 * Point Entity export 데이터
 */
export interface PointExportData {
  type: typeof SHAPES.POINT
  shapeId: string
  point: Vector2d
  balloonPoint: Vector2d
  color: string
  layerId: string
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
  dotType?: 'dot' | 'arrow'
}

/**
 * Point Entity를 export 데이터로 변환
 * @param entity - Entity 정보
 * @returns Export 데이터
 */
export function exportPointData(entity: {
  id: string
  position: Vector2d
  balloonPosition: Vector2d
  color: string
  layerId: string
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
  dotType?: 'dot' | 'arrow'
}): PointExportData {
  return {
    type: SHAPES.POINT,
    shapeId: entity.id,
    point: entity.position,
    balloonPoint: entity.balloonPosition,
    color: entity.color,
    layerId: entity.layerId,
    balloonFontColor: entity.balloonFontColor,
    balloonFillColor: entity.balloonFillColor,
    balloonScale: entity.balloonScale,
    connectedLineThicknessScale: entity.connectedLineThicknessScale,
    balloonFontScale: entity.balloonFontScale,
    pointShapeScale: entity.pointShapeScale,
    areaBorderThicknessScale: entity.areaBorderThicknessScale,
    dotType: entity.dotType,
  }
}
